import {
  assertEvent,
  assign,
  fromPromise,
  sendTo,
  setup,
  type SnapshotFrom,
} from 'xstate';

import {bookingMachine} from '../../machines/bookingMachine';
import {identificationMachine} from '../../machines/identificationMachine';
import {rsvpMachine} from '../../machines/rsvpMachine';
import {
  cancelEvent,
  getEvent,
  getEventTemplate,
  getSession,
  rescheduleRequest,
} from '../../services/schedularService';
import type {IParticipantType} from '../../types/IParticipantType';
import {getShortSha256Hash} from '../../utils/crypto';
import {isTimezoneSupported} from '../../utils/booking';

import type {
  IRootMachineContext,
  IRootMachineEvents,
  IRootMachineInput,
} from './rootMachine.types';
import {getRsvpStatus} from '../../utils/common';
import {isBrowserSupported} from '../../utils/browser';


export const rootMachine = setup({
  types: {
    context: {} as IRootMachineContext,
    events: {} as IRootMachineEvents,
    input: {} as IRootMachineInput,
  },
  actors: {
    getEvent: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getEvent(input.eventId),
    ),
    getSession: fromPromise(async ({input}: {input: {sessionId: string}}) =>
      getSession(input.sessionId),
    ),
    getTemplateBySuffix: fromPromise(
      async ({input}: {input: {agentId: string; suffix: string}}) =>
        getEventTemplate(input.agentId, input.suffix),
    ),
    getOwner: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; attendee?: string};
      }): Promise<IParticipantType | undefined> => {
        const guestHash = await getShortSha256Hash(`guest_${input.eventId}`);
        if (guestHash === input.attendee) {
          return 'CANDIDATE';
        }
        const hostHash = await getShortSha256Hash(`host_${input.eventId}`);
        if (hostHash === input.attendee) {
          return 'HOST';
        }
        return undefined;
      },
    ),
    cancelEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          reason: string;
          viewerTimezone: string;
          attendee: string;
        };
      }) =>
        cancelEvent(input.eventId, {
          viewer_timezone: input.viewerTimezone,
          attendee: input.attendee,
          reason: input.reason,
        }),
    ),
    sendRescheduleRequest: fromPromise(
      async ({input}: {input: {eventId: string; reason: string}}) =>
        rescheduleRequest(input.eventId, {
          reason: input.reason,
        }),
    ),
    generateAttendee: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getShortSha256Hash(`guest_${input.eventId}`),
    ),
    rsvpMachine,
    bookingMachine,
    identificationMachine,
  },
  guards: {
    isBrowserNotSupported: () => !isBrowserSupported(),
    isRescheduleLimitReached: ({context}) => {
      return context.eventDetails?.reschedule_attempts_left === 0;
    },
    isEditBooking: ({context}) => {
      if (!context.currentRoute) {
        return false;
      }
      return ['cancel', 'reschedule', 'rsvp'].includes(context.currentRoute);
    },
    isSession: ({context}) => !!context.sessionId,
    isCancellation: ({context}) => context.currentRoute === 'cancel',
    isReschedule: ({context}) => context.currentRoute === 'reschedule',
    isRsvp: ({context}) => context.currentRoute === 'rsvp',
    isCancellationNoticePass: ({context}) =>
      !!context?.eventDetails?.did_cancellation_notice_pass,
    isAttendeeAvailable: ({context}) => !!context.attendee,
    isHost: ({context}) => context.owner === 'HOST',
    isCandidate: ({context}) => context.owner === 'CANDIDATE',
    isSessionExpired: ({context}) => !!context?.session?.is_expired,
    isTimezoneSupported: ({context}) =>
      context.timezone ? isTimezoneSupported(context.timezone) : false,
    isBookingSuccess({event}) {
      assertEvent(event, 'xstate.done.actor.bookingFlow');
      return event.output.finalState === 'BookingSuccess';
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCcD2qAuBiANgQwDsoBXPGAOmIAcI8MwBtABgF1FQrVYBLDb1AuxAAPRAEYAbAHYALOSkBOBRLEKpADgDMAJnUSANCACeiALSbZ8gKyaFtqxplMpEqwF83htJnKwMeZGxmNiQQTh4+ASFRBE0JBXImMU1kmXVtMSkmCRkZQxMEbSZtcgltK3KpIqd1NI8vdAxff0CsBjEQji5efkFQmOl1cjEHONknDKsFfMRtF3IbGyqmTRSmdUl6kG8mvwCg7U6w7si+0AGFJnJ1BzF02znnTRnYqwlyZSkNbW0FG4lNFsduQ+ABbMAALwEYAAMqg8BBIG1WEJwj0ov1xDIpMMmDILBlahtilYXjl3uoFNoJM4pGJdJo3kDGiDuOCoQRYfDERA2h1USdetFEAoZFZyDIJLVsmkxPiKmTJdcqaMrMSysyfGDIdC4QikQxDgKIkLMYVRZpEkxRQ5tASrGIySorbVJQ5LmkJJqmtqOWAAArcADGAGswMgsL7ob4wBhgsb0WcRIhsVd8VI1doZFn8fcXjJ6fJc8UbtnnJtPNsWWAAG5gAgYPUQbhELAQaMtmuoMPkYG1+uN7ktqAITuoIN0XrBeOhNGnYUIZLi5ROAsSdeKLJSF5KBIZVZMKxveIOGTe8j9htN4dYcNoZDkKj4DAAM1QyFBverdavQ6Io4ILsJ1OacUVnQUMXOLEKnIH41TpTRrT+P4XjEZJ3iPTdMkuI9z1gOAeAEa9W3bTlyDHHtgXw2BCIIYiRzHYCp1YGcuhNSDkwQBQYOSLM-lkKZimmYxxCYdYPkyKw8WyXibDwgjeno29kHvR9nzfD8vx8ajaPogCgMnARQKOOdTSgxdqT3OIxKlS45XpF4NktPRMjddQBPSc9LwwAAVMBQSfOguQRG9SLAcjAO7cK+x-Xz-MC+g9MYwyCGMhN5zNX5D2uMUrEldItHUQ9UMPHEVGpNCxVWTR1C82K-IC59gubVs73fNS6A0z8YoHBqEua4d9PHFK0vA9ikxiHRpFKMUNDy-E7m3ESEA2OQFDQ1wN3Wr4FHPRF6A-FswAAeQAd05CMwoirtKJZfbw1BI6zouoamKMliwLYxMFzlVZ5D0UVqVkdQQe0VC7iubJLikVZuLsPK6oHeiAFEVPfLBkFjZAjFY45xoXAsEhUDZkgzBavjJLRYLEyYNDlJ55JoxTuQNT68e+s07BKSk5jsbNVizJaCmSQs0LeQ913pAt3ErKiFKIlneXaEyIIm8QqTTdzVmxPRteeZb6TmCVpCzMQxI0L5tEZ2jUfvDGsZxtnTI4mJ6T+D5JU+dbcl+fWCiqIYj3ibiLFTKVzwAI3QENhwAMRwVBTrbDtIp7KPuzjhPTtx521cKPLLRGdbJDUZJrVQ3J3l57FszSRkxUj6PM8T5OyIo8L05joh48T5X0rMziUl0BZEKWGHdB+P2sXiWDxYq9z3IqRGGz6prbfRzGMGxnPVYXaqMLGPEeeycoySUcgLDxOkHSzO1atlllE4uptWZV-GzTlcUXBkjbGWUOVUKDBHmoDY0gsgAkBA-HwT9wwvyVvyMaHNzK8QSGKFQNV6RHiklPRcUo5B4lGCoN45QvRQKaDA5AcC2hGkQRlcyRRCySlTI4Ko8QwYGyATYJQsgkg1Xgo3DORAADKxAgxBgIq3cK7ctJNE7sOERYiCKvRGh9N+SDOLlHctYdYdoTxFEZKhMmiQVBoUpG8BwORzwTgIOInAz5eg91OhFXgyI1F0M4uYMoEpKSqBWFMReBjlqTyuA6B08RDZlCpFYwgtj7ECEcc4oICCvruIGDiLQZtXB0gzGUHQLxgkLBGL8KoR5uIAmiTYsAdiUoJJbC4w0biB4xFqOki0cotBZFWELWYdoQlFLUOUKYbxIENB8NY2JNSs69jAHgWA8T3yfi6vbWZ8zNKwGIBHR6cYna70ytzHIjJfhTElCMWo+ScgJGyFMOYLhbDSAqRM04CTMYrIILHBZvgNlbL4CRFON1oosnGVUuJbypkvLmW8j56zNm8B+QxSKb1UqqP7i7MwBYL5ijErUKSUkF4GGWpobElobCwymHwpwUgHnAsmYnaZrz3lrK+bCm8bUHwJS6jI8gQLqlPLBTMiFDLPzQu+YNZKIFkW0KabMKosEaTJGUCsMSgSCi6GSNcLIuQ7CqHXFmKlPKHF8vpR81lyyBVQqZdsxpqLCjOiUJiioR5DwyG4vkuke5Cpij0ACZ155MawCDAAC0gMQHAYAEk4DZLwAAcpgAASjMwNr8UV514mmDMAdLaEuzPko8QxuKSXaebdyvq4CJogCGsNUyI1bJjRgeNeAy18itSmgEQxfZ0mxFkKSdwc2Mg+BmFYAIyiSCtmQ6Z-qg3ltDeGyNGBa31sbQ05NC4ii-AvtaJIpVXA3BzRmC+GxWHrgsHzEtE7g3TsNWazSSzwWrKFRane79kG-ApC0qSSgaRFXxSquwVwCqqFFCsOkYhT1lorc8-ld7PkwowHCyR10oqcr9WBi9tLb2QsZTBuFyjxUsEfeo12q6L6uFFLcykthAG2A+CkHaBUpiUrHchyd4HL1QeFcy1qaM2XqQ+cCJj57K1ocgxh+9WHRUIpUXhnZT7OLfFgjVDBhsHQrHybmYYzrbTFAJKQ0ZTR+NTsE049DgqLxcdNWxh90mCPiA2NzB0YosrZAli8NYYgPjrGU2kL4NVQPMdQ04uR3cs7wekYFqAjj8OpPVtNHVpVRQ5BWOofJzgSh2kZJmJcPnGOlr84Z8gYXHEhdTh3JuQXe7JPZlF2INU-2yE-XELQZR8loTc2keLdp1oqjPNls9BnakEHqVZqr9IpQLBcCMBweh6N5CCSkS0aWcwMLQiMqsPh9MsdpXUpJzafqIT3AqjQJJZr5NsDieCv1Li8IEV3KAABhGJVTBVYDC-dypOATPsctcuzKPMPh3x+EoOUtz8lzDc7IDQiFbIMmu8OV7tiRFid+W3YrnKXsPZwAjkV-4xXMSkztzKgHlRqE3dmDQwkf3rkSFmYZ8pfYw6IHDqpmOONQGUqpdlvGWRo7e8z2D4mDK4ci1K2IugcT02wghQd37ZjOoSCkLW20xJ3Blrp-LpW7vo-XhGbntiPuWfx+ZHQehSinNSH8LBJ38SJHSEfbi5Qao6dW00Og9ACCIjAAAcXrOGIKvJ1uhvIFAVAQvrX2-FNIISmQi74lQqRiUcpXBmziHccpY6Xf1nd17i6vusDcpBYH4PQ3heISNooA8khzFxGl4uOwlppCSGyA8WopDKwEFQIieAoQdjffMqYT13jOt+P4osHc4pnC2b8Wgo3eEWgYB75xVQYPuG2-xACTIqF0hjds0USka-uuq+IAQdZVBOCBEgAAITQKdfCyB58xGzOH08SQLbOHJ4gSk+DcgHrt2JZQ54oychwJ35YhDC5R8x6ABw3A4LyjyDVA1TlCv5kz-5sg6iciBihjhjAEIDSCWiKDFDOCbomL5hGxSSyDoQnJdrLyDghREBYFyglCODZBZA-CUgaCOR4jyCEo6COBmI3DWzMw0FQBYF2BDAqBShlBhJMLsFyAwz4hzBEjcR8FjreSrxBT0RYGOYfBqBYKbpYqOQ-CFIFhpDuQhwahjr3SHScjPSYGSrWryoexORrAEG6LgzYjUzWgVAWABy7TKGxQoxcZYHerqaHg-BfAgySjdLYFUgSS+IQKzTZj8EKz6gQDCFvClCHgixqh2rrCuE4hFAboFhaD2aJEEBa50F5StbKAO4AhZhJCkjLRkFU5gIpD4jOoqD07hZZwaHeyJDrSITOrpbYioRZhtqfCi6ZAf5UGqH0BlG2F5xxB2jDCMhFRhIkIOAvB0igE0bMHpBig+pjoUJAFzE-RpEHJlhzDmLKD1HCySglBqgjoA7oQbAlHIzCBUDcCYwpHHGcyZBWi6BqjrCk7rEGxpByAZIHLxAZgV4dEKLiI0QaGnjyYVC7g-DrgzbCwAiQz8y6BFQbqjqq5t58AvhGCwkERYGEoyoWx2gWxqCXDV5R5uYbC4pHKAlfB6ogqOJYF95mwD6+J-wBLXGzBXzWD5EpA1HxCO7Ah540pOJbZcmqoXzlD0HVTOo3CCmFBLizyLRAzuiZDskylcro4gr+ioARpBgFApLC7mBSTEbZgp50gdbrg5paJHiYpUhpB8T6m8pCZGofhck0jXAtZ3AtEwwrGqYzzZgka-BEjxBekGo+lXqLILJYEaCwRFxZjkg6rSA5ppFUjrjaFTDxDYhxnxKsYibQZY5CHfG96FgkymIhk1Y2CurRFzYLzwF2iSglmgoJlQaspYF5Q4ipjrRHg1TDmRGg7up3ItZYpmGq7SnemymIgNjcAvjBgykpncS4joTcRJBA6qZ0jWCEpOT0HWj4lO7jooaGaBHG53yKZwRJ7NY8lHpSg3AmEZhnl8Y5YCYzo1pxoJqTp0GIRXB25ZrYgkbKoy5fALC5Kwz4iborafm9YbZGbCaCopmEryaFRoT3kqazY2kXJlBShMFJC+bfllkmZdSAUwS6IpAjCL5JZBKLzUxZBFS2BFKSksj+55bGbmqI5VmWl2G-CMm0gAh-BFRQEubj4SjrA1TjzUnJCkV9bkXGoBHVkL5VG9G1F2jBlOr5IrgLA5jMFmxFwq7nlcUJIFZdFqWuyFmlBaCjyXB6DRnnJFS9HYLFCJ5lAMaq7mVTLcBLlElrkLkaGISiEPDbpKCgI5lDBOWfzWg+wjCKXIWJKAVKhMIbrKCXANbNbuySBxCrArFiTFBJX+YXm5YwizoLoAXWXiCuBXA5BQltE7QAgnaSCJCKBoIlh1EcVrawA1hUCck1U17rSwE5CSHuQTYVy5Czw0gVBOBaaKAdGM7vbJlDW-AGF4gnJTD0jpDsIqoEILD2YWzvq8RLXo685wrdFiQzQZC0hVSQnhmQwrhvBgVSi6B6qknwlrW-rqquCIRl4iHomIBHkUjj7ODyiYI9WyLq7LWzECXzFlAlDpUuDOo2TuSW6Wg6K24OoO7njxoACOxAcAdaX5Bmn1ne8NP0gyiQ5xIMDoHS1eyx9VRFHhh6rg546ebuYAnu3uyAvuCJagnBFQ5IrFaWG+WQ-0zqTg-iZODGHgQAA */
  context: ({input}) => ({
    id: input.id,
    currentRoute: input.currentRoute,
    attendee: input.attendee,
    aid: input.aid,
    timezone: input.timezone,
    sessionId: input.sessionId,
    language: input.language,
    countyCode: input.countyCode,
  }),
  id: 'root',
  initial: 'start',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => event.language,
      }),
    },
  },
  states: {
    start: {
      always: [
        {
          target: 'unsupportedBrowser',
          guard: {
            type: 'isBrowserNotSupported',
          },
        },
        {
          target: 'timezoneLoaded',
          guard: {
            type: 'isTimezoneSupported',
          },
        },
        {
          target: 'timezonePicker',
        },
      ],
    },

    unsupportedBrowser: {
      type: 'final',
    },

    timezoneLoaded: {
      always: [
        {
          target: 'eventLoading',
          guard: {
            type: 'isEditBooking',
          },
        },
        {
          target: 'sessionLoading',
          guard: {
            type: 'isSession',
          },
        },
        {
          target: 'eventTemplateLoading',
        },
      ],
    },

    timezonePicker: {
      on: {
        'timezone.set': {
          target: 'timezoneLoaded',
          actions: assign({
            timezone: ({event}) => event.timezone,
          }),
        },
      },
    },

    eventLoading: {
      invoke: {
        id: 'rootNew.eventLoading:invocation[0]',
        input: ({context: {id}}) => ({eventId: id}),
        onDone: {
          target: 'determineOwner',
          actions: assign({
            eventDetails: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventLoadingError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getEvent',
      },
    },

    sessionLoading: {
      invoke: {
        input: ({context}) => {
          if (context.sessionId) {
            return {
              sessionId: context.sessionId,
            };
          }
          throw new Error('sessionId not found');
        },
        onDone: {
          target: 'sessionLoaded',
          actions: assign({
            session: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'sessionError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getSession',
      },
    },

    eventTemplateLoading: {
      invoke: {
        input: ({context: {currentRoute, id}}) => ({
          agentId: currentRoute,
          suffix: id,
        }),
        onDone: {
          target: 'bookingFlow',
          actions: assign({
            eventTemplate: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventTemplateError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getTemplateBySuffix',
      },
    },

    determineOwner: {
      invoke: {
        id: 'rootNew.determineOwner:invocation[0]',
        input: ({context}) => {
          if (context.eventDetails?.id) {
            return {
              eventId: context?.eventDetails?.id,
              attendee: context?.attendee,
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'ownerLoaded',
          actions: assign({
            owner: ({event}) => event.output,
          }),
        },
        src: 'getOwner',
      },
    },

    eventLoadingError: {
      on: {
        retry: {
          target: 'eventLoading',
        },
      },
    },

    sessionLoaded: {
      always: [
        {
          target: 'sessionExpired',
          guard: {
            type: 'isSessionExpired',
          },
        },
        {
          target: 'eventTemplateLoading',
        },
      ],
      entry: assign({
        language: ({context}) =>
          context.session?.guest_language || context.language,
      }),
    },

    sessionError: {
      on: {
        retry: {
          target: 'sessionLoading',
        },
      },
    },

    bookingFlow: {
      on: {
        'language.update': {
          actions: [
            assign({
              language: ({event}) => event.language,
            }),
            sendTo(
              ({system}) => system.get('bookingMachine')!,
              ({event}) => ({
                type: 'language.update',
                language: event.language,
              }),
            ),
          ],
        },
      },
      invoke: {
        id: 'bookingFlow',
        input: ({context: {timezone, eventTemplate, language, session}}) => {
          if (timezone) {
            return {
              timezone,
              eventTemplate,
              language,
              session,
            };
          }
          throw new Error('timezone not found');
        },
        src: 'bookingMachine',
        systemId: 'bookingMachine',
        onDone: [
          {
            target: 'bookingSuccess',
            actions: assign({
              eventDetails: ({event}) => event.output.eventDetails,
            }),
            guard: {
              type: 'isBookingSuccess',
            },
          },
          {
            target: 'notifySuccess',
          },
        ],
      },
    },

    eventTemplateError: {
      on: {
        retry: {
          target: 'eventTemplateLoading',
        },
      },
    },

    ownerLoaded: {
      always: [
        {
          target: 'cancellationFlow',
          guard: {
            type: 'isCancellation',
          },
        },
        {
          target: 'rescheduleFlow',
          guard: {
            type: 'isReschedule',
          },
        },
        {
          target: 'rsvpFlow',
          guard: {
            type: 'isRsvp',
          },
        },
      ],
      entry: assign({
        language: ({context}) => {
          const users =
            context.owner === 'HOST'
              ? context?.eventDetails?.interviewers
              : context?.eventDetails?.candidates;

          const currentUser = users?.find((user) => user.id === context.aid);
          return currentUser?.language || context.language;
        },
      }),
    },

    sessionExpired: {
      type: 'final',
    },

    bookingSuccess: {
      invoke: {
        input: ({context: {eventDetails}}) => ({
          eventId: eventDetails?.id || '',
        }),
        src: 'generateAttendee',
        onDone: {
          target: 'attendeeGenerated',
          actions: assign({
            attendee: ({event}) => event.output,
            owner: 'CANDIDATE',
          }),
        },
      },
    },
    notifySuccess: {
      type: 'final',
    },
    cancellationFlow: {
      initial: 'init',
      states: {
        init: {
          always: [
            {
              target: 'cancellationPolicy',
              guard: {
                type: 'isCancellationNoticePass',
              },
            },
            {
              target: 'reasonForm',
              guard: {
                type: 'isAttendeeAvailable',
              },
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        cancellationPolicy: {
          type: 'final',
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
            submitting: {
              entry: assign({
                reason: ({event}) => {
                  assertEvent(event, 'reasonForm.submit');
                  return event.reason;
                },
              }),
              invoke: {
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  if (context.eventDetails?.id) {
                    return {
                      eventId: context.eventDetails?.id,
                      reason: event.reason,
                      viewerTimezone:
                        context.eventDetails?.primary_guest_timezone || '',
                      attendee: context.attendee || '',
                    };
                  }
                  throw new Error('eventId not found');
                },
                onDone: {
                  target: '#root.cancelSuccess',
                },
                onError: {
                  target: 'error',
                  actions: assign({
                    error: ({event}) => event.error,
                  }),
                },
                src: 'cancelEvent',
              },
            },
            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.cancellationFlow.identificationFlow:invocation[0]',
            input: ({context: {eventDetails}}) => {
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'cancel',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
      },
    },

    rescheduleFlow: {
      initial: 'init',
      states: {
        limitNotReached: {
          always: [
            {
              target: 'reasonForm',
              guard: {
                type: 'isHost',
              },
            },
            {
              target: 'bookingFlow',
              guard: {
                type: 'isCandidate',
              },
              actions: assign({
                isReschedule: true,
              }),
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                  actions: assign({
                    reason: ({event}) => event.reason,
                  }),
                },
              },
            },

            submitting: {
              invoke: {
                id: 'rootNew.rescheduleFlow.rescheduleRequest.submitting:invocation[0]',
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  if (context.eventDetails?.id) {
                    return {
                      eventId: context.eventDetails?.id,
                      reason: event.reason,
                    };
                  }
                  throw new Error('eventId not found');
                },
                onDone: {
                  target: '#root.RequestRescheduleSuccess',
                  reenter: true,
                },
                onError: {
                  target: 'error',
                  actions: assign({error: ({event}) => event.error}),
                },
                src: 'sendRescheduleRequest',
              },
            },

            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        bookingFlow: {
          on: {
            'language.update': {
              actions: [
                assign({
                  language: ({event}) => event.language,
                }),
                sendTo(
                  ({system}) => system.get('bookingFlow'),
                  ({event}) => ({
                    type: 'language.update',
                    language: event.language,
                  }),
                ),
              ],
            },
          },
          invoke: {
            id: 'bookingFlow',
            input: ({
              context: {timezone, eventDetails, attendee, language},
            }) => {
              if (timezone) {
                return {
                  timezone,
                  eventDetails,
                  attendee,
                  language,
                };
              }
              throw new Error('timezone not found');
            },
            onDone: [
              {
                target: '#root.bookingSuccess',
                actions: assign({
                  eventDetails: ({event}) => event.output.eventDetails,
                }),
                guard: {
                  type: 'isBookingSuccess',
                },
              },
              {
                target: '#root.notifySuccess',
              },
            ],
            src: 'bookingMachine',
            systemId: 'bookingMachine',
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.rescheduleFlow.identificationFlow:invocation[0]',
            input: ({context: {eventDetails}}) => {
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'reschedule',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
        init: {
          always: [
            {
              target: 'rescheduleLimitReached',
              guard: 'isRescheduleLimitReached',
            },
            {
              target: 'limitNotReached',
              reenter: true,
            },
          ],
        },

        rescheduleLimitReached: {},
      },
    },

    rsvpFlow: {
      invoke: {
        id: 'rootNew.rsvpFlow:invocation[0]',
        input: ({context}) => {
          const {eventDetails, aid} = context;
          const rsvpStatus = getRsvpStatus(eventDetails, aid, context.owner);
          if (aid && eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              aid,
              rsvpStatus,
            };
          }
          throw new Error('aid and eventId not found');
        },
        src: 'rsvpMachine',
        systemId: 'rsvpMachine',
      },
    },

    bookingCancelForm: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
          actions: assign({
            reason: ({event}) => event.reason,
          }),
        },
      },
    },

    bookingCancelSubmitting: {
      invoke: {
        input: ({event, context}) => {
          assertEvent(event, 'bookingCancelForm.submit');
          if (context.eventDetails?.id) {
            return {
              eventId: context.eventDetails?.id,
              reason: event.reason,
              viewerTimezone:
                context.eventDetails?.primary_guest_timezone || '',
              attendee: context.attendee || '',
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'cancelSuccess',
        },
        onError: {
          target: 'bookingCancelError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'cancelEvent',
      },
    },

    cancelSuccess: {
      type: 'final',
    },

    bookingCancelError: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
        },
      },
    },

    RequestRescheduleSuccess: {
      type: 'final',
    },

    attendeeGenerated: {
      on: {
        'reschedule.go': {
          target: 'rescheduleFlow',
        },
        'cancellation.go': {
          target: 'bookingCancelForm',
        },
      },
    },
  },
});

export type IRootMachineSnapshot = SnapshotFrom<typeof rootMachine>;
